// The partnership grid on teams#index: the value toggle and the hover tooltip.
//
// Purely presentational. Every figure is already on each cell as data
// attributes, so switching views reorders nothing and fetches nothing -- it
// only changes which number and colour a cell shows. Opens on Average round.

const TeamMatrix = {
  init() {
    const root = document.querySelector('[data-team-matrix]');
    if (!root || root.dataset.matrixReady === 'true') return;
    root.dataset.matrixReady = 'true';

    root.querySelectorAll('[data-view]').forEach((button) => {
      button.addEventListener('click', () => setView(root, button.dataset.view));
    });

    root.addEventListener('mouseover', (event) => {
      const cell = event.target.closest('td[data-row]');
      if (cell) describe(root, cell);
    });
    root.addEventListener('mouseleave', () => hideTip(root));
    root.querySelector('[data-matrix-wrap]')?.addEventListener('mouseleave', () => hideTip(root));

    setView(root, 'average');
  }
};

function setView(root, view) {
  root.querySelectorAll('[data-view]').forEach((button) => {
    const on = button.dataset.view === view;
    // .faso-seg styles the selected segment from aria-pressed.
    button.setAttribute('aria-pressed', String(on));
  });

  root.querySelectorAll('td[data-rounds] a').forEach((link) => {
    const cell = link.closest('td');
    // The rounds colours are inline on the link from the server, so they are
    // stashed on first switch and restored from the stash after that.
    if (!cell.dataset.tone) cell.dataset.tone = link.style.background;
    if (!cell.dataset.ink) cell.dataset.ink = link.style.color;

    const shown = {
      rounds:  [cell.dataset.rounds,  cell.dataset.tone,        cell.dataset.ink],
      best:    [cell.dataset.best,    cell.dataset.bestTone,    cell.dataset.bestInk],
      average: [cell.dataset.average, cell.dataset.averageTone, cell.dataset.averageInk]
    }[view];

    link.textContent = shown[0];
    link.style.background = shown[1];
    link.style.color = shown[2];
  });

  legend(root, view);
}

// Swatches and end labels both follow the cells -- a blue ramp under red cells
// labels the wrong scale. Rounds and best are single ramps; the average
// diverges, so its left half is the grey over-par side and its right half the
// Bright Blue under-par side. Each mirrors the alpha the view computes per cell.
function legend(root, view) {
  const swatches = [...root.querySelectorAll('[data-legend-ramp] i')];
  const last = swatches.length - 1;

  swatches.forEach((swatch, index) => {
    const position = index / last;
    if (view === 'rounds') {
      swatch.style.background = swatch.dataset.roundsTone;
    } else if (view === 'best') {
      swatch.style.background = `rgba(51,86,170,${(0.14 + position * 0.86).toFixed(2)})`;
    } else if (index < swatches.length / 2) {
      const over = 1 - index / (swatches.length / 2 - 1);
      swatch.style.background = `rgba(108,112,135,${(0.12 + over * 0.55).toFixed(2)})`;
    } else {
      const under = (index - swatches.length / 2) / (swatches.length / 2 - 1);
      swatch.style.background = `rgba(51,86,170,${(0.14 + under * 0.86).toFixed(2)})`;
    }
  });

  const ENDS = {
    rounds:  ['Fewer rounds', 'More'],
    best:    ['Closer to par', 'Further under'],
    average: ['Over par', 'Under par']
  }[view];

  const low = root.querySelector('[data-legend-low]');
  const high = root.querySelector('[data-legend-high]');
  if (!low || !high) return;

  // The rounds labels carry a count from the server; keep the original text.
  low.dataset.rounds = low.dataset.rounds || low.textContent;
  high.dataset.rounds = high.dataset.rounds || high.textContent;

  low.textContent = view === 'rounds' ? low.dataset.rounds : ENDS[0];
  high.innerHTML = view === 'rounds' ? high.dataset.rounds : ENDS[1];
}

// Hover: band the row and column headings, and show the tooltip above the cell
// -- names, then avg / best / worst / rounds for a pair, or why there is none.
function describe(root, cell) {
  root.querySelectorAll('th.on').forEach((heading) => heading.classList.remove('on'));
  root.querySelector(`th[data-row="${cell.dataset.row}"]`)?.classList.add('on');
  root.querySelector(`th[data-col="${cell.dataset.col}"]`)?.classList.add('on');

  const wrap = root.querySelector('[data-matrix-wrap]');
  const tip = root.querySelector('[data-matrix-tip]');
  if (!wrap || !tip) return;

  const set = (field, value) => {
    const el = tip.querySelector(`[data-tip-field="${field}"]`);
    if (el) el.textContent = value || '';
  };
  set('names', cell.dataset.names);

  const stats = tip.querySelector('[data-tip-stats]');
  if (cell.dataset.rounds) {
    set('detail', cell.dataset.years);
    set('average', cell.dataset.average);
    set('best', cell.dataset.best);
    set('worst', cell.dataset.worst);
    set('rounds', cell.dataset.rounds);
    // Worst shows once the cells carry it.
    tip.querySelector('[data-tip-worst]')?.classList.toggle('hidden', !cell.dataset.worst);
    stats.classList.remove('hidden');
  } else {
    set('detail', cell.dataset.blank === 'never-drawn'
      ? 'Never drawn together'
      : 'Never played the same tournament');
    stats.classList.add('hidden');
  }

  // Centred above the cell, kept inside the wrapper's width.
  tip.classList.remove('hidden');
  const box = wrap.getBoundingClientRect();
  const rect = cell.getBoundingClientRect();
  const half = tip.offsetWidth / 2;
  const x = Math.min(Math.max(rect.left + rect.width / 2 - box.left, half), box.width - half);
  tip.style.left = `${x}px`;
  tip.style.top = `${rect.top - box.top - 4}px`;
}

function hideTip(root) {
  root.querySelector('[data-matrix-tip]')?.classList.add('hidden');
  root.querySelectorAll('th.on').forEach((heading) => heading.classList.remove('on'));
}

// Bound to both events: turbolinks:load alone misses the first hard load in
// some paths, and DOMContentLoaded alone never fires again on in-app
// navigation. Every other src/*.js here does the same.
document.addEventListener('DOMContentLoaded', () => TeamMatrix.init());
document.addEventListener('turbolinks:load', () => TeamMatrix.init());

// Turbolinks caches the DOM as it stands, data-matrix-ready included, so
// without this a restored page comes back flagged and with no listeners.
document.addEventListener('turbolinks:before-cache', () => {
  document.querySelectorAll('[data-team-matrix]').forEach((root) => {
    delete root.dataset.matrixReady;
  });
});
